/* eslint-disable i18next/no-literal-string */
import CheckboxGroup from '@ingka/checkbox-group';
import FormField from '@ingka/form-field';
import type { RetailUnit } from '@lotta/shared/types-lottery-schemas';
import type { Control, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Values = Pick<RetailUnit, 'integrityCodes'>;

export type ControlledLegalTermsCheckboxesProps<TFormData extends Values = Values> = {
  control: Control<TFormData>;
  errorMessage: string | undefined;
  integrityCodes: RetailUnit['integrityCodes'];
  onUpdate: (id: string, checked: boolean) => void;
};

export const ControlledLegalTermsCheckboxes = <TFormData extends Values = Values>({
  errorMessage,
  control,
  integrityCodes,
  onUpdate,
}: ControlledLegalTermsCheckboxesProps<TFormData>) => {
  const { t } = useTranslation();

  const integrityCodesCheckBoxes = [
    {
      id: 'termsAndConditions',
      name: 'termsAndConditions',
      label: t('form-sign-up-config-terms-and-conditions-checkbox-label'),
    },
    {
      id: 'contestRules',
      name: 'contestRules',
      label: t('form-sign-up-config-contest-rules-checkbox-label'),
    },
    {
      id: 'privacyPolicy',
      name: 'privacyPolicy',
      label: t('form-sign-up-config-privacy-policy-checkbox-label'),
    },
  ] as const;

  if (errorMessage) {
    errorMessage = `${t(errorMessage)}`;
  }

  return (
    <FormField
      shouldValidate={!!errorMessage}
      valid={!errorMessage}
      validation={{
        msg: errorMessage,
        type: 'error',
      }}
    >
      {integrityCodes && (
        <Controller
          render={({ field: { onChange, onBlur, value } }) => (
            <CheckboxGroup
              name={`${t('form-sign-up-config-checkbox-group-title')}`}
              list={integrityCodesCheckBoxes.map((item) => ({
                ...item,
                checked: value?.includes(item.id) || integrityCodes.includes(item.id),
                disabled: integrityCodes.includes(item.id),
                onBlur,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  if (Array.isArray(value)) {
                    const valuesWithoutThisId = value.filter((id) => id !== item.id);
                    if (e.currentTarget.checked) {
                      onChange([...valuesWithoutThisId, item.id]);
                    } else {
                      onChange(valuesWithoutThisId);
                    }
                    onUpdate(item.id, e.currentTarget.checked);
                  }
                },
              }))}
            />
          )}
          name={'integrityCodes' as Path<TFormData>}
          control={control}
        />
      )}
    </FormField>
  );
};
